import { auth } from '../config/firebase';

export interface Link {
  id: string;
  oldURL: string;
  newURL: string;
  brandId: string;
  userId: string;
  userName: string;
  userPicture: string;
  brandName: string;
  brandLogo: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  clickCount: number;
  lastClicked: string | null;
  metadata: {
    source: string;
    version: string;
  };
}

export interface LinksAnalytics {
  totalClicks: number;
  uniqueUsers: number;
  clicksByDay: {
    date: string;
    clicks: number;
    uniqueUsers: number;
  }[];
  days: number;
  fetchedAt: string;
}

export interface LinksResponse {
  success: boolean;
  links: Link[];
  count: number;
  TotalClicks?: number;
  analytics?: LinksAnalytics;
}

export interface CreateLinkRequest {
  oldURL: string;
  newURL: string;
  brandId: string;
}

export interface CreateLinkResponse {
  success: boolean;
  link: Link;
}

export interface CheckSlugResponse {
  success: boolean;
  slug: string;
  available: boolean;
  taken: boolean;
  message: string;
  link_details?: {
    id: string;
    newURL: string;
    oldURL_preview: string;
  };
}

export interface UpdateLinkStatusResponse {
  success: boolean;
  link?: Link;
  message?: string;
}

export type LinkStatus = 'active' | 'archived' | 'inactive' | 'pending' | 'expired';

// Payload for updating general link details (excluding status, which has its own method)
export interface UpdateLinkPayload {
  oldURL?: string; // The new destination URL
  brandId?: string;
  // newURL?: string; // The short link itself can be added here if API supports general update for it
}

export const linksService = {
  async getLinks(): Promise<LinksResponse> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch('https://api.socialness.ai/management/links', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch links');
    }

    return response.json();
  },

  async createLink(data: CreateLinkRequest): Promise<CreateLinkResponse> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch('https://api.socialness.ai/management/links/create', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Create link API error:', errorText);
      throw new Error(`Failed to create link: ${response.status}`);
    }

    return response.json();
  },

  async deleteLink(linkId: string): Promise<{ success: boolean }> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`https://api.socialness.ai/management/links/${linkId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete link API error:', errorText);
      throw new Error(`Failed to delete link: ${response.status}`);
    }

    return response.json();
  },

  async checkSlugAvailability(slug: string, token: string): Promise<CheckSlugResponse> {
    const response = await fetch(`https://api.socialness.ai/management/links/check-slug/${slug}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to check slug and parse error response.' }));
      throw new Error(errorData.message || `HTTP error ${response.status}: Failed to check slug availability.`);
    }

    return response.json();
  },

  async updateLinkStatus(linkId: string, newStatus: LinkStatus): Promise<UpdateLinkStatusResponse> {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`https://api.socialness.ai/management/links/${linkId}?_method=PATCH`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: newStatus })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `Failed to update link status to ${newStatus} and parse error response.` }));
      throw new Error(errorData.message || `HTTP error ${response.status}: Failed to update link status to ${newStatus}.`);
    }

    return response.json();
  },

  async updateLink(linkId: string, payload: UpdateLinkPayload): Promise<Link> { 
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`https://api.socialness.ai/management/links/${linkId}?_method=PATCH`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Failed to update link and could not parse error response.');
      console.error('Update link API error:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || `Failed to update link: ${response.status}`);
      } catch (e) {
        throw new Error(`Failed to update link: ${response.status} - ${errorText}`);
      }
    }
    return response.json(); 
  }
}; 